<template>
  <div>
    <div v-if="isLoading" class="flex items-center justify-center h-[100vh]">
      <spinner size="medium" />
    </div>
    <iframe 
      v-show="!isLoading && !hasError"
      ref="reportFrame"
      :key="jumpUrl" 
      :src="jumpUrl" 
      :style="{ height: '100vh', width: '100%' }" 
      frameBorder="no"
      @load="handleIframeLoad"
      @error="handleIframeError"
    />
    <div v-if="hasError" class="flex flex-col items-center justify-center h-[100vh]">
      <fluent-icon size="medium" icon="error" class="mb-2 text-red-500" />
      <p class="text-base text-slate-700 mb-4">{{ $t('ERRORS.REPORT_LOAD_ERROR') }}</p>
      <woot-button
        variant="link"
        size="small"
        icon="refresh"
        @click="retryLoading"
      >
        {{ $t('RETRY') }}
      </woot-button>
    </div>
  </div>
</template>

<script>
import { fetchToken } from './helpers';
import Spinner from 'shared/components/Spinner.vue';
import FluentIcon from 'shared/components/FluentIcon/Icon.vue';

export default {
  name: 'CustomDetails',
  components: {
    Spinner,
    FluentIcon,
  },
  data() {
    return {
      jumpUrl: '',
      token: '',
      isLoading: true,
      hasError: false,
      retryCount: 0,
      maxRetries: 3,
    };
  },
  computed: {
    baseUrl() {
      const routeName = this.$route.name;
      
      // Default URL for regular reports
      const defaultUrl = 'https://data.mayfair-inc.com/bi/#/external/dashboard/37606?inline=true&appId=1&sheetId=3378&feature=%7B%22dashboard%22%3A%7B%22showTitle%22%3Atrue%2C%22showOwner%22%3Afalse%2C%22showActions%22%3Afalse%2C%22sheetType%22%3A%22part%22%2C%22sheetList%22%3A%5B3383%5D%2C%22showHeader%22%3Atrue%2C%22showFavor%22%3Afalse%2C%22actions%22%3A%5B%5D%2C%22report%22%3A%7B%22showTitle%22%3Atrue%2C%22showTips%22%3Afalse%2C%22showToolbar%22%3Afalse%2C%22showHeader%22%3Atrue%2C%22enableEnterVizQuery%22%3Atrue%2C%22actions%22%3A%5B%22excelExport%22%2C%22FullScreen%22%2C%22imgExport%22%2C%22copyToDashboard%22%2C%22embed%22%2C%22comment%22%2C%22viewReport%22%2C%22createMonitor%22%2C%22UserContract%22%2C%22Refresh%22%5D%7D%2C%22enableComment%22%3Afalse%2C%22sheet%22%3A%7B%22sheetList%22%3A%5B3383%5D%7D%7D%7D';
      
      // Check if it's a simplified report route
      if (routeName === 'simplified_custom_details') {
        const { id: currentUserId } = this.$store.getters.getCurrentUser;
        const currentUserIdStr = currentUserId.toString();
        const hpTLIds = window.urConfig.hpVendorTLIds ? JSON.parse(window.urConfig.hpVendorTLIds).map(id => id.toString()) : [];
        if (hpTLIds.includes(currentUserIdStr)) {
          return 'https://data.mayfair-inc.com/bi/#/external/dashboard/37624?inline=true&appId=1&sheetId=3406&feature=%7B%22dashboard%22%3A%7B%22showTitle%22%3Afalse%2C%22showOwner%22%3Afalse%2C%22showActions%22%3Afalse%2C%22sheetType%22%3A%22part%22%2C%22sheetList%22%3A%5B3406%5D%2C%22showHeader%22%3Afalse%2C%22showFavor%22%3Afalse%2C%22actions%22%3A%5B%5D%2C%22report%22%3A%7B%22showTitle%22%3Atrue%2C%22showTips%22%3Afalse%2C%22showToolbar%22%3Afalse%2C%22showHeader%22%3Atrue%2C%22enableEnterVizQuery%22%3Atrue%2C%22actions%22%3A%5B%22excelExport%22%2C%22FullScreen%22%2C%22imgExport%22%2C%22copyToDashboard%22%2C%22embed%22%2C%22comment%22%2C%22viewReport%22%2C%22createMonitor%22%2C%22UserContract%22%2C%22Refresh%22%5D%7D%2C%22enableComment%22%3Afalse%2C%22sheet%22%3A%7B%22sheetList%22%3A%5B3406%5D%7D%7D%7D'; // URL1 - To be updated
        }
        
        const estsnyaTLIds = window.urConfig.EstsnyaTLId?.split(',')?.map(id => id.trim());
        if (estsnyaTLIds.includes(currentUserIdStr)) {
          return 'https://data.mayfair-inc.com/bi/#/external/dashboard/37625?inline=true&appId=1&sheetId=3411&feature=%7B%22dashboard%22%3A%7B%22showTitle%22%3Afalse%2C%22showOwner%22%3Afalse%2C%22showActions%22%3Afalse%2C%22sheetType%22%3A%22part%22%2C%22sheetList%22%3A%5B3411%5D%2C%22showHeader%22%3Afalse%2C%22showFavor%22%3Afalse%2C%22actions%22%3A%5B%5D%2C%22report%22%3A%7B%22showTitle%22%3Atrue%2C%22showTips%22%3Afalse%2C%22showToolbar%22%3Afalse%2C%22showHeader%22%3Atrue%2C%22enableEnterVizQuery%22%3Atrue%2C%22actions%22%3A%5B%22excelExport%22%2C%22FullScreen%22%2C%22imgExport%22%2C%22copyToDashboard%22%2C%22embed%22%2C%22comment%22%2C%22viewReport%22%2C%22createMonitor%22%2C%22UserContract%22%2C%22Refresh%22%5D%7D%2C%22enableComment%22%3Afalse%2C%22sheet%22%3A%7B%22sheetList%22%3A%5B3411%5D%7D%7D%7D'; // URL2 - To be updated
        }
      }
      
      return defaultUrl;
    },
    country() {
      return this.$route.params.country || 'cn_inner';
    },
    site() {
      return this.$route.params.site || 'default';
    },
  },
  mounted() {
    this.initializeReport();
  },
  methods: {
    async initializeReport() {
      this.isLoading = true;
      this.hasError = false;
      
      try {
        // 获取token
        this.token = await fetchToken();
        
        // 更新URL
        const countryCode = this.country === 'cn_inner' ? 'oasis' : this.country;
        const queryString = `&lang=en_US&filter=country eq ${countryCode} and brand eq ${this.site} and is_label eq 1`;
        
        // 使用延迟设置URL，给浏览器更多的时间进行渲染处理
        setTimeout(() => {
          this.jumpUrl = this.token 
            ? `${this.baseUrl}${queryString}${this.token}` 
            : `${this.baseUrl}${queryString}`;
        }, 300);
      } catch (error) {
        console.error('Error loading label report:', error);
        this.handleIframeError();
      }
    },
    handleIframeLoad() {
      // 使用延迟隐藏加载状态，确保iframe已完全加载
      setTimeout(() => {
        this.isLoading = false;
        this.retryCount = 0;
      }, 200);
    },
    handleIframeError() {
      this.isLoading = false;
      this.hasError = true;
      console.error('Failed to load label report iframe');
    },
    retryLoading() {
      if (this.retryCount < this.maxRetries) {
        this.retryCount += 1;
        this.initializeReport();
      } else {
        this.$store.dispatch('notifications/create', {
          type: 'error',
          message: this.$t('ERRORS.REPORT_LOAD_ERROR_MAX_RETRY'),
        });
      }
    },
  },
  beforeUnmount() {
    // 确保在组件卸载前清理iframe引用，防止内存泄漏
    this.jumpUrl = '';
    this.$refs.reportFrame = null;
  },
};
</script>
